import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Select from "react-select";
import { toast } from "../Toast";
import { RolesService } from "../../services";
import { Button, Modal } from "react-bootstrap";
import i18next from "i18next";

const triggerEventOptions = [
    {value: 'alert-acked', label: 'Alert Acknowledged'}
];

const triggerActionOptions = [
    {value: 'SEND_TO_DIST_GROUP', label: 'Send To Distribution Group'}
];

const distributionTypes = [
    {value: 'sms', label: 'SMS'}
];

const SendToDistributionGroupActionType = ({sinkIndex, updateSinkContext, existingSinks}) => {
    const [availableDistributionGroups, setAvailableDistributionGroups] = useState([]);
    const [selectedDistributionType, setSelectedDistributionType] = useState(null);
    const [selectedDistributionGroup, setSelectedDistributionGroup] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);

        RolesService.listAllForRoleType("group_role").then((distRoles) => {
            const roleOptions = distRoles.roles.map((role) => ({ value: role.id, label: role.name, distributionGroup: role }));;
            setAvailableDistributionGroups(roleOptions);

            if (existingSinks && existingSinks[sinkIndex]) {
                const selectedDT = distributionTypes.find((distributionType) => {
                    return distributionType.value == existingSinks[sinkIndex].context.notificationType;
                });
                setSelectedDistributionType(selectedDT);

                const selectedDG = roleOptions.find((roleOption) => {
                    return roleOption.value == parseInt(existingSinks[sinkIndex].context.distributionGroupId);
                });
                setSelectedDistributionGroup(selectedDG);
            }

            setLoading(false);
        }).catch((err) => {
            console.error(err);
            setLoading(false);
            toast.error("Error loading distribution groups.");
        });
    }, [existingSinks]);

    useEffect(() => {
        if (selectedDistributionType && selectedDistributionGroup) {
            updateSinkContext(sinkIndex, {
                "notificationType":`${selectedDistributionType.value}`,
                "distributionGroupId":`${selectedDistributionGroup.value}`
            });
        }
    }, [selectedDistributionGroup, selectedDistributionType]);

    return loading ? (<div className="loader">Loading...</div>) : (
        <div className="col-sm-12" style={{paddingTop: 15}}>
            <div className="row">
                <div className="col-sm-6">
                    <label>Distribution Type</label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={distributionTypes}
                        clearable={false}
                        value={selectedDistributionType}
                        onChange={setSelectedDistributionType}
                        searchable={false}
                    />
                </div>

                <div className="col-sm-6">
                    <label>Distribution Group</label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={availableDistributionGroups}
                        clearable={false}
                        value={selectedDistributionGroup}
                        onChange={setSelectedDistributionGroup}
                        searchable={false}
                    />
                </div>
            </div>
        </div>
    );
};

SendToDistributionGroupActionType.propTypes = {
    sinkIndex: PropTypes.number.isRequired,
    updateSinkContext: PropTypes.func.isRequired,
    existingSinks: PropTypes.array,
};

const EventWorkflowConfigurationForm = ({ me, onSaveWorkflowConfiguration, originalConfiguration, onDeleteWorkflowConfiguration }) => {
    const [selectedEventType, setSelectedEventType] = useState(null);
    const [selectedTriggerActionType, setSelectedTriggerActionType] = useState(null);
    const [workflowName, setWorkflowName] = useState("");
    const [submitReady, setSubmitReady] = useState(false);
    const [sinks, setSinks] = useState([]);
    const [existingSinks, setExistingSinks] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        setSubmitReady(workflowName != null && workflowName != "" && selectedEventType != null && selectedTriggerActionType != null && sinks.length > 0);
    }, [workflowName, selectedEventType, selectedTriggerActionType, sinks]);
    
    useEffect(() => {
        if (originalConfiguration) {
            setWorkflowName(originalConfiguration.name);

            const config = JSON.parse(originalConfiguration.configuration);

            const selectedTEO = triggerEventOptions.find((option) => {
                return config.predicates.find((predicate) => predicate.predicate.pathExpression && predicate.predicate.pathExpression.includes(option.value));
            });
            setSelectedEventType(selectedTEO);

            const selectedET = triggerActionOptions.find((option) => {
                return config.sinks.find((sink) => sink.type == option.value);
            });
            setSelectedTriggerActionType(selectedET);
            setSinks(config.sinks);
            setExistingSinks([...config.sinks]);
        }
    }, [originalConfiguration]);

    const updateSinkContext = (sinkIndex, sinkContext) => {
        let sinkCopy = [...sinks];
        sinkCopy[sinkIndex] = {
            id: `${Date.now()}`,
            type: `${selectedTriggerActionType.value}`,
            context: sinkContext,
        };
        sinkCopy[sinkIndex].context["templateType"] = selectedEventType?.value;
        setSinks(sinkCopy);
    };

    const saveWorkflowConfiguration = () => {
        if (selectedEventType == null) {
            toast.error('Workflow Trigger is required.');
            return;
        }

        const workflowPayload = {
            "enterpriseToken":me?.enterprise?.token,
            "source":{
                "type":"REDIS",
                "name":"ops:eventstream:v1"
            },
            "predicates":[{
                "id":`${Date.now()}`,
                "sinkRefs": [sinks[0].id],
                "predicate":{
                    "type":"JSON_PATH",
                    "pathExpression":`$.[?(@.eventType=='${selectedEventType.value}')]`
                }
            }],
            "sinks":sinks
        };

        onSaveWorkflowConfiguration({
            id: originalConfiguration?.id,
            type: "WORKFLOW",
            name: workflowName,
            workflow: JSON.stringify(workflowPayload),
        });
    }

    const deleteWorkflow = () => {
        onDeleteWorkflowConfiguration(originalConfiguration.id);
    };

    return (
        <div>
            <div className="row">
                <div className="col-sm-6">
                    <label>Workflow Name</label>
                    <input style={{ width: "100%" }} type="text" value={workflowName} onChange={({ target }) => setWorkflowName(target.value)} />
                </div>
            </div>

            <div className="row" style={{ paddingTop: 20 }}>
                <div className="col-sm-6">
                    <label>Workflow Trigger</label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={triggerEventOptions}
                        clearable={false}
                        value={selectedEventType}
                        onChange={setSelectedEventType}
                        searchable={false}
                    />
                </div>
            </div>

            <div className="row" style={{ paddingTop: 20 }}>
                <div className="col-sm-6">
                    <label>Workflow Action</label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={triggerActionOptions}
                        clearable={false}
                        value={selectedTriggerActionType}
                        onChange={setSelectedTriggerActionType}
                        searchable={false}
                    />
                </div>

                {
                    selectedTriggerActionType?.value == "SEND_TO_DIST_GROUP" && 
                    <SendToDistributionGroupActionType 
                        sinkIndex={0} 
                        updateSinkContext={updateSinkContext} 
                        existingSinks={existingSinks}
                    />
                }
            </div>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Workflow: {workflowName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{i18next.t('messages.are-you-sure-delete')}</p> <br />
                </Modal.Body>
                <Modal.Footer>
                    <a className="btn btn-danger" onClick={deleteWorkflow}>{i18next.t('delete')}</a>
                    <Button onClick={() => setShowDeleteModal(false)}>{i18next.t('form-labels.cancel')}</Button>
                </Modal.Footer>
            </Modal>
            
            <div className="row" style={{ paddingTop: 20 }}>
                <div className="col-sm-12">
                    <div className="pull-right">
                        <button disabled={!submitReady} className="btn btn-default" onClick={saveWorkflowConfiguration}>Save</button>
                        { onDeleteWorkflowConfiguration && <span>&nbsp;&nbsp;<a className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>{i18next.t("delete")}</a></span> }
                    </div>
                </div>
            </div>
        </div>
    );
};

EventWorkflowConfigurationForm.propTypes = {
    me: PropTypes.object.isRequired,
    onSaveWorkflowConfiguration: PropTypes.func.isRequired,
    originalConfiguration: PropTypes.object,
    enableDelete: PropTypes.bool
};

export default connect((state) => ({me: state.user.me}))(EventWorkflowConfigurationForm);
